/**
 * The choices of one Block Kit select that searches (contracts apps.ts): people, conversations and channels are
 * searched on the server a page at a time (`kitOptions`, logic/blockKit `kitPickerQuery`), external selects ask the
 * app through the server (`externalOptions`, after `minQueryLength` characters). Static selects never need this.
 *
 * - Typing replaces the list after a short pause (200 ms); answers to an older query are dropped.
 * - `loadMore` appends the next page (server pickers only; apps answer in one go, like Slack).
 * - `labelOf` names a chosen value: every option seen is remembered, and `resolve` looks up ids it hasn't seen
 *   (initial values) with `ids`.
 *
 * Without the API (old clients, tests) `available` is false and the platform lists the cached workspace instead.
 */
import type { ExternalOptionsRequest, KitPickerOption } from "@bundle/contracts";
import { type KitOptionGroupNode, type KitOptionNode, type KitSelectNode, externalQueryReady, kitPickerQuery } from "../logic/blockKit.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** Where the select is (external selects: the server finds the element there). */
export type KitContainer = { messageId: string } | { viewId: string } | { homeAppId: string };

export type KitPickerRow = KitOptionNode & { kind?: KitPickerOption["kind"] };

export type KitPickerState = {
  query: string;
  options: KitPickerRow[];
  groups?: KitOptionGroupNode[];
  loading: boolean;
  /** Another page exists (server pickers). */
  hasMore: boolean;
  /** External selects: characters still to type before the app is asked (0 = asking). */
  needsChars: number;
  failed: boolean;
};

const DEBOUNCE_MS = 200;

export class KitPickerViewModel extends ViewModel<KitPickerState> {
  private readonly ws: WorkspaceViewModel;
  private readonly node: KitSelectNode;
  private readonly container: KitContainer | undefined;
  private readonly blockId: string;
  private readonly labels = new Map<string, string>();
  private cursor: string | undefined;
  private gen = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(ws: WorkspaceViewModel, node: KitSelectNode, blockId: string, container?: KitContainer) {
    super({ query: "", options: [], loading: false, hasMore: false, needsChars: node.source === "external" ? Math.max(0, node.minQueryLength ?? 3) : 0, failed: false });
    this.ws = ws;
    this.node = node;
    this.blockId = blockId;
    this.container = container;
    for (const o of node.options) this.labels.set(o.value, o.label);
    this.onDispose(() => clearTimeout(this.timer));
  }

  /** The server can search for this select. */
  get available(): boolean {
    if (this.node.source === "external") return this.ws.deps.api.externalOptions !== undefined && this.container !== undefined;
    return this.ws.deps.api.kitOptions !== undefined && kitPickerQuery(this.node, "") !== undefined;
  }

  /** The picker opened: the first page (or, for external selects, nothing until enough is typed). */
  start(): void {
    this.search(this.state.query, true);
  }

  setQuery(query: string): void {
    this.update({ query });
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.search(query, true), DEBOUNCE_MS);
  }

  loadMore(): void {
    if (!this.state.hasMore || this.state.loading) return;
    this.search(this.state.query, false);
  }

  labelOf(value: string): string | undefined {
    return this.labels.get(value);
  }

  /** Looks up labels for chosen ids it hasn't seen (people / conversations / channels). */
  resolve(ids: readonly string[]): void {
    const missing = ids.filter((id) => !this.labels.has(id));
    const q = kitPickerQuery(this.node, "");
    const api = this.ws.deps.api;
    if (missing.length === 0 || q === undefined || api.kitOptions === undefined) return;
    api.kitOptions(this.ws.wsId, { ...q, ids: missing.slice(0, 100).join(",") }).then(
      (res) => {
        for (const o of res.options) this.labels.set(o.value, o.label);
        if (!this.isDisposed) this.setState({ ...this.state });
      },
      () => undefined,
    );
  }

  private remember(list: readonly KitOptionNode[]): void {
    for (const o of list) this.labels.set(o.value, o.label);
  }

  private search(query: string, fresh: boolean): void {
    const api = this.ws.deps.api;
    const gen = ++this.gen;
    if (fresh) this.cursor = undefined;
    if (this.node.source === "external") {
      if (api.externalOptions === undefined || this.container === undefined) return;
      const need = Math.max(0, (this.node.minQueryLength ?? 3) - query.trim().length);
      if (!externalQueryReady(this.node, query)) {
        this.setState({ ...this.state, options: [], loading: false, hasMore: false, needsChars: need, failed: false });
        return;
      }
      this.update({ loading: true, needsChars: 0, failed: false });
      const req: ExternalOptionsRequest = { ...this.container, blockId: this.blockId, actionId: this.node.actionId, value: query };
      api.externalOptions(this.ws.wsId, req).then(
        (res) => {
          if (gen !== this.gen || this.isDisposed) return;
          const options = res.options.map((o) => ({ value: o.value, label: o.text.text }));
          const groups = res.optionGroups?.map((g) => ({ label: g.label.text, options: g.options.map((o) => ({ value: o.value, label: o.text.text })) }));
          const all = [...options, ...(groups ?? []).flatMap((g) => g.options)];
          this.remember(all);
          // A picked option reaches the app with its text (state.values / block_actions), like Slack.
          this.ws.rememberKitOptions(this.blockId, this.node.actionId, all);
          const next: KitPickerState = { ...this.state, options, loading: false, hasMore: false };
          if (groups !== undefined) next.groups = groups;
          else delete next.groups;
          this.setState(next);
        },
        () => {
          if (gen === this.gen && !this.isDisposed) this.update({ loading: false, failed: true });
        },
      );
      return;
    }
    const q = kitPickerQuery(this.node, query);
    if (q === undefined || api.kitOptions === undefined) return;
    this.update({ loading: true, failed: false });
    const cursor = this.cursor;
    api.kitOptions(this.ws.wsId, cursor === undefined ? q : { ...q, cursor }).then(
      (res) => {
        if (gen !== this.gen || this.isDisposed) return;
        const page: KitPickerRow[] = res.options.map((o) => ({ value: o.value, label: o.label, kind: o.kind }));
        this.remember(page);
        this.cursor = res.nextCursor;
        this.setState({ ...this.state, options: fresh ? page : [...this.state.options, ...page], loading: false, hasMore: res.nextCursor !== undefined });
      },
      () => {
        if (gen === this.gen && !this.isDisposed) this.update({ loading: false, failed: true });
      },
    );
  }
}
